import { BuyerGroup } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { ExchangeRates } from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import handleNaN from './handleNaN';

interface BuyerStats {
  divisionsParticipating: number;
  newDivisionCount: number;
  totalDivisionCount: number;
  clearingEarn: number;
  clearingInvoiceAmount: number;
  acceptancePercentage: number;
  awardedInvoiceAmount: number;
  avgDaysPaidEarly: number;
  eligibleDpeWeightedSum: number;
  eligibleInvoiceAmount: number;
  eligibleInvoiceAmountUsd: number;
  eligibleInvoiceCount: number;
  estimatedEarlyPaymentAmount: number;
  nextPayDate: string | null;
  nextPayDateWithEligibleInvoices: string | null;
  nextGoodFundsDate: string | null;
  nextGoodFundsDateWithEligibleInvoices: string | null;
  takerExcludedInvoiceAmount: number;
  takerExcludedInvoiceCount: number;
  totalInvoiceAmount: number;
  totalInvoiceAmountUsd: number;
  totalInvoiceCount: number;
}

interface BuyerStatsAccumulator extends BuyerStats {
  clearingInvoiceAmountEnabled: number;
  eligibleInvoiceAmountEnabled: number;
}

const getBuyerStats = ({
  buyer,
  exchangeRates,
  isNewDivision,
  selectedCurrency,
  includeAwardedInvoices,
  getNewDivisionAlertEligible,
}: {
  buyer: BuyerGroup;
  exchangeRates: ExchangeRates;
  isNewDivision: (userAddedToDivision?: string) => boolean;
  selectedCurrency: string;
  includeAwardedInvoices: boolean;
  getNewDivisionAlertEligible?: (takerMarkets: TakerMarket[]) => TakerMarket[];
}): BuyerStats => {
  const accumulator = buyer.takerMarkets.reduce(
    (acc: BuyerStatsAccumulator, curr) => {
      // calculate eligible invoice count as eligible invoice count + awarded invoice count when includeAwardedInvoices is true for current taker market
      const eligibleInvoiceCountForTakerMarket =
        curr.eligibleInvoiceCount + (includeAwardedInvoices ? curr.awardedInvoiceCount : 0);

      // division counts
      acc.divisionsParticipating += curr.offerConfig.isEnabled ? 1 : 0;
      acc.newDivisionCount +=
        isNewDivision(curr.userAddedToDivision) ||
        (getNewDivisionAlertEligible && getNewDivisionAlertEligible?.([curr]).length > 0)
          ? 1
          : 0;
      acc.totalDivisionCount += 1;

      // stats
      acc.awardedInvoiceAmount += convertCurrency({
        amount: curr.awardedInvoiceAmount,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      acc.clearingEarn += convertCurrency({
        amount: includeAwardedInvoices ? curr.clearingEarn : curr.acceptedEarn,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      const clearingAmount = convertCurrency({
        amount: includeAwardedInvoices ? curr.clearingInvoiceAmount : curr.acceptedInvoiceAmount,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      const eligibleAmount = convertCurrency({
        amount: curr.eligibleInvoiceAmount + (includeAwardedInvoices ? curr.awardedInvoiceAmount : 0),
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });

      acc.clearingInvoiceAmount += clearingAmount;
      acc.eligibleDpeWeightedSum += convertCurrency({
        amount: curr.eligibleDpeWeightedSum + (includeAwardedInvoices ? curr.awardedDpeWeightedSum : 0),
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      acc.eligibleInvoiceAmount += eligibleAmount;
      acc.eligibleInvoiceAmountUsd +=
        curr.eligibleInvoiceAmountUsd + (includeAwardedInvoices ? curr.awardedInvoiceAmountUsd : 0);
      acc.eligibleInvoiceCount += curr.eligibleInvoiceCount + (includeAwardedInvoices ? curr.awardedInvoiceCount : 0);
      acc.estimatedEarlyPaymentAmount = acc.clearingInvoiceAmount - acc.clearingEarn;
      acc.avgDaysPaidEarly = acc.eligibleDpeWeightedSum / acc.eligibleInvoiceAmount;
      acc.takerExcludedInvoiceAmount += convertCurrency({
        amount: curr.takerExcludedInvoiceAmount,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      acc.takerExcludedInvoiceCount += curr.takerExcludedInvoiceCount;
      acc.totalInvoiceAmount += convertCurrency({
        amount: curr.totalInvoiceAmount,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      acc.totalInvoiceAmountUsd += curr.totalInvoiceAmountUsd;
      acc.totalInvoiceCount += curr.totalInvoiceCount;

      // only include enabled offers in acceptance percentage calculation
      if (curr.offerConfig.isEnabled) {
        acc.clearingInvoiceAmountEnabled += clearingAmount;
        acc.eligibleInvoiceAmountEnabled += eligibleAmount;
        acc.acceptancePercentage = handleNaN(acc.clearingInvoiceAmountEnabled / acc.eligibleInvoiceAmountEnabled);
      }

      // get the soonest marketPayDate across all takerMarkets
      if (curr.marketPayDate) {
        if (!acc.nextPayDate || curr.marketPayDate < acc.nextPayDate) {
          acc.nextPayDate = curr.marketPayDate;
        }
      }

      // get the soonest marketPayDate with eligible invoices across all takerMarkets
      if (curr.marketPayDate && eligibleInvoiceCountForTakerMarket > 0) {
        if (!acc.nextPayDateWithEligibleInvoices || curr.marketPayDate < acc.nextPayDateWithEligibleInvoices) {
          acc.nextPayDateWithEligibleInvoices = curr.marketPayDate;
        }
      }

      // get the soonest marketGoodFundsDate across all takerMarkets
      if (curr.marketGoodFundsDate) {
        if (!acc.nextGoodFundsDate || curr.marketGoodFundsDate < acc.nextGoodFundsDate) {
          acc.nextGoodFundsDate = curr.marketGoodFundsDate;
        }
      }

      // get the soonest marketGoodFundsDate with eligible invoices across all takerMarkets
      if (curr.marketGoodFundsDate && eligibleInvoiceCountForTakerMarket > 0) {
        if (
          !acc.nextGoodFundsDateWithEligibleInvoices ||
          curr.marketGoodFundsDate < acc.nextGoodFundsDateWithEligibleInvoices
        ) {
          acc.nextGoodFundsDateWithEligibleInvoices = curr.marketGoodFundsDate;
        }
      }

      return acc;
    },
    {
      // division counts
      divisionsParticipating: 0,
      newDivisionCount: 0,
      totalDivisionCount: 0,
      // stats
      acceptancePercentage: 0,
      awardedInvoiceAmount: 0,
      clearingEarn: 0,
      clearingInvoiceAmount: 0,
      clearingInvoiceAmountEnabled: 0,
      avgDaysPaidEarly: 0,
      eligibleDpeWeightedSum: 0,
      eligibleInvoiceAmount: 0,
      eligibleInvoiceAmountEnabled: 0,
      eligibleInvoiceAmountUsd: 0,
      eligibleInvoiceCount: 0,
      estimatedEarlyPaymentAmount: 0,
      nextPayDate: null,
      nextPayDateWithEligibleInvoices: null,
      nextGoodFundsDate: null,
      nextGoodFundsDateWithEligibleInvoices: null,
      takerExcludedInvoiceAmount: 0,
      takerExcludedInvoiceCount: 0,
      totalInvoiceAmount: 0,
      totalInvoiceAmountUsd: 0,
      totalInvoiceCount: 0,
    }
  );

  // Remove internal tracking properties before returning
  const {
    clearingInvoiceAmountEnabled: _clearingInvoiceAmountEnabled,
    eligibleInvoiceAmountEnabled: _eligibleInvoiceAmountEnabled,
    ...stats
  } = accumulator;
  return stats;
};

export default getBuyerStats;
